import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import type { EditorActions } from '@atlaskit/editor-core';
import LockLockedIcon from '@atlaskit/icon/core/migration/lock-locked--lock-circle';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { VariableResponse } from '../../../../services/notification-variables/index.tsx';
import messages from './messages';

export const lockIconTestId = 'languageSettings-notificationEditor-dropdown-lockCircleIcon';

export const EditorDropdownMenu = ({
	variableGroups,
	safeNotificationSettingsEnabled,
	editorActions,
}: {
	variableGroups?: VariableResponse[];
	safeNotificationSettingsEnabled: boolean;
	editorActions?: Pick<EditorActions, 'appendText'>;
}) => {
	const { formatMessage } = useIntl();

	const onClickEditorActions = useCallback(
		(item: { key: string }) => {
			if (editorActions) {
				const editorContent = `\${${item.key}}`;
				editorActions.appendText(editorContent);
			}
		},
		[editorActions],
	);

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...dropdownProps }) => (
				<Button {...dropdownProps} appearance="link" ref={triggerRef}>
					{formatMessage(messages.insertVariableTriggerNonFinal)}
				</Button>
			)}
		>
			{variableGroups?.map((group, groupIndex) => (
				<DropdownItemGroup key={groupIndex}>
					{group.variables.map((item, itemIndex) => (
						<DropdownItem
							key={itemIndex}
							onClick={() => {
								onClickEditorActions(item);
							}}
						>
							<ItemWrapper>
								{item.label}
								{safeNotificationSettingsEnabled && !!item.privacyConcerns.length && (
									<ToolTip
										content={formatMessage(messages.hiddenFromEmailContentNonFinal)}
										position="right"
									>
										<LockLockedIcon label="" color={token('color.icon')} testId={lockIconTestId} />
									</ToolTip>
								)}
							</ItemWrapper>
						</DropdownItem>
					))}
				</DropdownItemGroup>
			))}
		</DropdownMenu>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div': {
		marginLeft: token('space.100', '8px'),
	},
	display: 'flex',
	alignItems: 'center',
	margin: `0 ${token('space.100', '8px')}`,
});
